'use client'

import { Carousel, CarouselContent, CarouselItem } from '@lastbrand/web-ui/carousel'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useCallback, useMemo, type ReactNode } from 'react'

import { HeroBannerDS, toHeroDSRichTextItems } from '@src/components/blocks/hero-banner-ds'
import type { IProductCard } from '@src/components/product-card/product-card.types'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { ProductCard } from '@src/ds/blocks/product-card'
import { useContentWithBackgroundImageProps } from '@src/quince-components/src'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useDynamicStyle from '@src/quince-site-common/src/hooks/useDynamicStyle'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import { getValidPromo } from '@src/quince-site-common/src/utils/festivalOffersUtility'
import { SectionProps } from '@src/SectionRenderer/SectionRenderer.types'
import styles from '@src/SectionRenderer/sections/halfBannerHalfProductTiles/halfBannerHalfProductTiles.module.scss'
// Shared hero-banner overlay positioning (bottom-anchored, left-aligned) — the same classes the
// heroBannerV2 DS adapter applies, so the banner here matches the legacy `<HeroBannerV2>` it renders.
import {
  heroV2ButtonWrapperClass,
  heroV2ContentClass,
  heroV2ContentInnerClass,
  heroV2WrapperClass,
} from '@src/SectionRenderer/sections/heroBannerV2/hero-banner-v2-ds-styles'
import {
  getContentContainerTrackingMetadata,
  getTrackingContext,
} from '@src/SectionRenderer/sections/productListSection/productListSection.helpers'
import { getFindingMethodAggregate } from '@src/telemetry/findingMethod'

import type { SystemStyleObject } from '@lastbrand/web-ui-styled-system/types'

/** Legacy parity: only the first 6 product cards render, regardless of how many the CMS sends. */
export const MAX_PRODUCTS_TO_SHOW = 6

/** Legacy parity: per-tile tracker suffix. */
export const TRACKER_SUFFIX = '__product_category_carousel'

/** Legacy parity: banner baseDynamicClass injected for Contentful theming. */
const BASE_DYNAMIC_HEIGHT = 'height--595'

/* eslint-disable i18next/no-literal-string -- Panda token ids + CSS keywords, not user copy */
export const containerClass = css({
  width: 'full',
  display: 'flex',
  justifyContent: 'center',
  backgroundColor: 'paperwhite',
  px: { base: '0', lg: 'space-48' },
})

export const innerContainerClass = css({
  width: 'full',
  maxWidth: '1504px',
  display: 'flex',
  gap: { base: 'space-16', lg: 'space-24' },
  flexDirection: { base: 'column', lg: 'row' },
  // `stretch` (legacy `.container` default) makes both halves equal height on desktop — the product grid
  // drives the height and the banner stretches to match. (Legacy used `flex-start` here, which left the
  // square banner taller than the grid.)
  alignItems: 'stretch',
  py: { base: '0', lg: 'space-64' },
})

export const bannerSlotClass = css({
  width: { base: 'full', lg: '50%' },
  // Mobile (stacked column): keep the square so the banner has a height. Desktop (row): no forced ratio —
  // height comes from `align-items: stretch` (= the product-grid height), so the two halves match.
  aspectRatio: { base: '1 / 1', lg: 'auto' },
  position: 'relative',
  overflow: 'hidden',
})

// Banner wrapper fills its slot (square on mobile, stretched-to-grid on desktop).
const bannerFillClass = css({ height: 'full' })
// The image is taken OUT OF FLOW (`position: absolute; inset: 0`) so the banner column contributes no
// intrinsic height — otherwise the image's natural height would make the banner taller than the grid even
// with `align-items: stretch`. Removed from flow, the product grid drives the row height and the absolutely
// positioned image cover-fills the stretched slot.
const bannerImageCoverClass = css({
  position: 'absolute',
  inset: '0',
  width: 'full',
  height: 'full!',
  objectFit: 'cover',
} as SystemStyleObject)

export const productsSlotClass = css({
  width: { base: 'full', lg: '50%' },
})

const gridClass = css({
  display: 'grid',
  width: 'full',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  columnGap: 'space-24',
  rowGap: 'space-48',
})

const carouselWrapperClass = css({
  width: 'full',
  containerType: 'inline-size',
  pb: 'space-32',
  pl: 'space-16',
  '& [data-slot="carousel-content"]': {
    gap: 'space-8',
  },
  '& [data-slot="carousel-item"]': {
    minWidth: 0,
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: '26cqi',
  },
})
/* eslint-enable i18next/no-literal-string */

export const ProductTileGrid = ({ tiles }: { tiles: ReactNode[] }) => (
  <div className={gridClass} data-testid={TEST_LOCATORS.HALF_BANNER_HALF_PRODUCT_TILES.PRODUCT_TILE_GRID}>
    {tiles}
  </div>
)

export const ProductTileCarousel = ({ tiles }: { tiles: ReactNode[] }) => {
  const opts = useMemo(
    () =>
      ({
        align: 'start',
        slidesToScroll: 1,
        containScroll: 'trimSnaps',
        dragFree: true,
      }) as const,
    [],
  )
  return (
    <div
      className={carouselWrapperClass}
      data-testid={TEST_LOCATORS.HALF_BANNER_HALF_PRODUCT_TILES.PRODUCT_TILE_CAROUSEL}
    >
      <Carousel opts={opts}>
        <CarouselContent>
          {tiles.map((tile, idx) => (
            <CarouselItem key={idx}>{tile}</CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  )
}

/**
 * Banner half — the full `HeroBannerDS` assembly (tracker-enriched sectionConfig, shared heroV2
 * overlay classes, out-of-flow cover-fill treatment for image AND video). Exported so the PL v2
 * nested container (`widgets/half-banner-half-product-tiles`) renders the exact same banner
 * without re-deriving this wiring; `tracker` arrives pre-suffixed (`...${TRACKER_SUFFIX}`).
 */
export const HalfBannerBannerDs = ({
  sectionConfig,
  tracker,
}: {
  sectionConfig: SectionProps['sectionConfig']
  tracker: string
}) => {
  const { id, trackingMetaData, sectionType, sectionIndex } = sectionConfig

  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP

  const enrichedTrackingMetaData = useMemo(
    () => ({
      context: {
        ...trackingMetaData?.context,
        col: sectionIndex,
        sectionId: id,
        sectionType,
      },
    }),
    [trackingMetaData, sectionIndex, id, sectionType],
  )

  const bannerSectionConfig = useMemo(
    () => ({ ...sectionConfig, tracker, baseDynamicClasses: [BASE_DYNAMIC_HEIGHT] }),
    [sectionConfig, tracker],
  )

  const contentWithBackgroundImageProps = useContentWithBackgroundImageProps(bannerSectionConfig)

  const heroBannerProps = useMemo(
    () => ({
      ...contentWithBackgroundImageProps,
      // Route the banner's Contentful rich-text styling through the shared HeroBannerDS converter,
      // same as the heroBannerV2 DS adapter (STRFRNT-14943 converters).
      richTextsItems: toHeroDSRichTextItems(contentWithBackgroundImageProps.richTextsItems),
      imageProps: {
        ...contentWithBackgroundImageProps.imageProps,
        // @i18n-ignore
        loading: 'eager' as const,
        url: contentWithBackgroundImageProps.imageProps?.url ?? '',
        title: contentWithBackgroundImageProps.imageProps?.title ?? '',
        className: [bannerImageCoverClass, contentWithBackgroundImageProps.imageProps?.className]
          .filter(Boolean)
          .join(' '),
      },
      // Apply the same out-of-flow cover treatment to the video branch: HeroBannerDS renders <Video> via
      // videoProps.className, so without this a video banner stays in flow and breaks the equal-height
      // (align-items: stretch) layout the way the image branch is fixed above. Only added when a video is
      // actually present so we don't synthesize an empty videoProps.
      ...(contentWithBackgroundImageProps.videoProps && {
        videoProps: {
          ...contentWithBackgroundImageProps.videoProps,
          className: [bannerImageCoverClass, contentWithBackgroundImageProps.videoProps.className]
            .filter(Boolean)
            .join(' '),
        },
      }),
      tracker,
      trackingMetaData: enrichedTrackingMetaData,
      overrideMaxImageWidth1600: isDesktop,
      fireImageClickEventAlways: true,
      deviceType,
    }),
    [contentWithBackgroundImageProps, tracker, enrichedTrackingMetaData, isDesktop, deviceType],
  )

  return (
    <HeroBannerDS
      {...heroBannerProps}
      className={[heroV2WrapperClass, bannerFillClass, heroBannerProps.className].filter(Boolean).join(' ')}
      contentClassName={heroV2ContentClass}
      contentInnerWrapper={heroV2ContentInnerClass}
      buttonsWrapperClassName={heroV2ButtonWrapperClass}
    />
  )
}

/**
 * DS implementation of the `halfBannerHalfProductTiles` home-page section.
 *
 * Replaces the legacy SCSS/Swiper UI with `HeroBannerDS` + a mini-variant
 * `ProductCard` grid (desktop) / carousel (mobile). Selection between this
 * widget and the legacy implementation is owned by `HalfBannerHalfProductTiles.tsx`
 * via `shouldUseDesignSystem()`.
 */
const HalfBannerHalfProductTilesWidgetDs = ({ sectionConfig }: SectionProps) => {
  const {
    tracker: baseTracker,
    classNames = [],
    classNamesMobileOverrides = [],
    baseDynamicClasses = [],
    mobileDynamicClassesOverrides = [],
    id,
    trackingMetaData,
    sectionType,
    productCards = [],
  } = sectionConfig

  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP
  const { className: wrapperClassName, styleObject: wrapperStyle } = useDynamicStyle({
    mobileClassNamesOverride: classNamesMobileOverrides,
    baseClassNames: classNames,
    baseDynamicValueClasses: baseDynamicClasses,
    dynamicValueClassesMobileOverride: mobileDynamicClassesOverrides,
    postFix: styles.dynamicClassesPostFix ?? '',
  })

  // Legacy parity: `${tracker}__product_category_carousel` is built unconditionally —
  // when `baseTracker` is undefined the string is `"undefined__product_category_carousel"`.
  // Preserved verbatim so existing dashboards keep receiving the same tracker value.
  const tracker = `${baseTracker}${TRACKER_SUFFIX}`

  const getIsVisibleTrackParams = useCallback(() => ({ ...trackingMetaData }), [trackingMetaData])
  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  const contentContainerTrackingMetadata = useMemo(
    () => getContentContainerTrackingMetadata(trackingMetaData),
    [trackingMetaData],
  )

  const products = useMemo(() => productCards.slice(0, MAX_PRODUCTS_TO_SHOW), [productCards])

  const renderTile = useCallback(
    (productCard: IProductCard, idx: number) => {
      const trackingContext = {
        ...getTrackingContext(tracker, productCard.product.slug),
        tracker,
      }
      const validPromo = getValidPromo(productCard.product.cardVariants[0]?.promoConfig)

      const productClickTrackConfig = {
        trackInternal: true,
        trackGA: true,
        ...trackingContext,
        context: {
          sectionType,
          sectionid: id,
          ...trackingMetaData?.context,
          productPosition: idx,
          ...contentContainerTrackingMetadata,
          findingMethod: getFindingMethodAggregate(),
          is_final_sale: validPromo?.isFinalSale ? 1 : 0,
          is_app_exclusive: productCard.product.cardVariants[0]?.displayConfig.appExclusive ? 1 : 0,
          productProperties: {
            discounted_price: validPromo?.promoPrice ?? productCard.product.cardVariants[0]?.price,
            price: productCard.product.cardVariants[0]?.price,
          },
        },
      }

      const wishlistTrackConfig = {
        ...trackingContext,
        sectionType,
        sectionid: id,
        ...trackingMetaData?.context,
        productPosition: idx,
        ...contentContainerTrackingMetadata,
        findingMethod: getFindingMethodAggregate(),
      }

      return (
        <ProductCard
          key={`${productCard.product.productId}`}
          data-testid={TEST_LOCATORS.HALF_BANNER_HALF_PRODUCT_TILES.PRODUCT(idx)}
          productCard={productCard}
          dsVariant="mini"
          tracker={tracker}
          hideWishlistIcon={!isDesktop}
          hideBuyNowIcon={!isDesktop}
          hideTitle
          hideTags
          hideSubscriptionPrice
          priceCompareAtFirst
          productClickTrackConfig={productClickTrackConfig}
          wishlistTrackConfig={wishlistTrackConfig}
        />
      )
    },
    [tracker, sectionType, id, trackingMetaData, contentContainerTrackingMetadata, isDesktop],
  )

  const tiles = useMemo(() => products.map(renderTile), [products, renderTile])

  return (
    <div
      ref={trackIsVisibleRef}
      className={[containerClass, wrapperClassName].filter(Boolean).join(' ')}
      style={wrapperStyle}
      data-testid={TEST_LOCATORS.HALF_BANNER_HALF_PRODUCT_TILES.ROOT}
    >
      <div className={innerContainerClass}>
        <div className={bannerSlotClass}>
          <HalfBannerBannerDs sectionConfig={sectionConfig} tracker={tracker} />
        </div>
        <div className={productsSlotClass}>
          {isDesktop ? <ProductTileGrid tiles={tiles} /> : <ProductTileCarousel tiles={tiles} />}
        </div>
      </div>
    </div>
  )
}

export default HalfBannerHalfProductTilesWidgetDs
